import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { HubConnectionState } from '@microsoft/signalr';
import { BehaviorSubject } from 'rxjs';
import { ApiService } from './api.service';
import { State, toStateArray } from '../model/state.model';
import { isPlayerStateNullMessage, isPlayerStateNullState } from './player-state-error';

@Injectable({
    providedIn: 'root'
})
export class SignalRService {
    private hubConnection: signalR.HubConnection;
    public statesToProcess: BehaviorSubject<State[] | null> = new BehaviorSubject(null);
    private connectionStartPromise: Promise<void> | null = null;
    private reconnectingForUser: string | null = null;

  constructor() {}

  public startConnection = (username: string): Promise<void> => {
    if (this.hubConnection && this.hubConnection.state === HubConnectionState.Connected) {
      return Promise.resolve();
    }

    if (this.connectionStartPromise !== null && this.reconnectingForUser === username) {
      return this.connectionStartPromise;
    }

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${ApiService.baseUrl}/AppHub?username=${username}`, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets
      })
      .build();
    this.addMessageListener();

    this.reconnectingForUser = username;
    this.connectionStartPromise = this.hubConnection.start()
      .finally(() => {
        this.connectionStartPromise = null;
      });

    return this.connectionStartPromise;
  }

  public disconnect = () => {
    if (this.hubConnection) {
      this.hubConnection.stop()
      .then(() => console.log('SignalR Connection stopped'));
    }
    this.connectionStartPromise = null;
    this.reconnectingForUser = null;
  }

  public addMessageListener = () => {
    this.hubConnection.off('ReceiveMessage');
    this.hubConnection.on('ReceiveMessage', (message: string) => {
      try {
        // console.log('ReceiveMessage', message);
        if (!message || !message.length) {
          return;
        }
        // Transient "player has no state yet" diagnostic: not actionable for users.
        if (isPlayerStateNullMessage(message)) {
          return;
        }
        // Some server branches can return plain diagnostic strings; ignore them.
        if (message.startsWith('MyDjPlayerState_')) {
          console.warn('ReceiveMessage skipped non-JSON payload', message);
          return;
        }

        // Legacy JSONP wrapper: callbackName(<json>);
        const firstParenIndex = message.indexOf('(');
        if (firstParenIndex > 0 && message.endsWith(');')) {
          message = message.substring(firstParenIndex + 1, message.length - 2);
        } else if (message[0] === '(' && message.endsWith(');')) {
          // Legacy variant where payload is wrapped as (<json>);
          message = message.substring(1, message.length - 2);
        }

        // Prefer strict JSON first. Legacy key-normalization fallback is kept for old payloads.
        let parsed: unknown;
        try {
          parsed = JSON.parse(message);
        } catch {
          const correctJson = message.replace(/(['"])?([a-z0-9A-Z_]+)(['"])?:/g, '"$2": ');
          parsed = JSON.parse(correctJson);
        }
        // A single state object (`{"id":1,"scheduler":{...}}`) is a valid answer too; consumers
        // iterate the packet, so normalize it to an array instead of dropping it.
        const states: State[] = toStateArray(parsed);
        // Ignore transient backend bootstrap status: it is not actionable for users.
        if (Array.isArray(states) && states.length > 0 && states.every(isPlayerStateNullState)) {
          return;
        }
        // console.log('states', states);
        this.statesToProcess.next(states);
      } catch (err) {
        if (isPlayerStateNullMessage(message)) {
          return;
        }
        const scheduler = this.tryExtractSchedulerFromMalformedPayload(message);
        if (scheduler) {
          // Best-effort fallback: keep scheduler page working even when full player state is malformed.
          this.statesToProcess.next(([{ scheduler }] as unknown) as State[]);
          return;
        }
        console.error("ReceiveMessage error", err, message);
      }
    });
  }

  public sendMessage = (user: string, message: string) => {
    // console.log('sendMessage', message);
    if (this.hubConnection && this.hubConnection.state === HubConnectionState.Connected) {
      this.hubConnection.invoke('SendMessage', user, message)
      .catch(err => console.error(err));
    } else {
      // Expected during startup or app resume: connection not ready yet; we retry below.
      this.startConnection(user)
        .then(() => {
          return this.hubConnection.invoke('SendMessage', user, message);
        })
        .catch(err => console.error('sendMessage Error establishing SignalR connection: ' + err));
    }
  }

  private tryExtractSchedulerFromMalformedPayload(payload: string): any | null {
    if (!payload || typeof payload !== 'string') {
      return null;
    }

    const schedulerByKey = this.extractObjectByPropertyName(payload, 'scheduler');
    if (schedulerByKey && schedulerByKey.includes('"weeklySchedule"') && schedulerByKey.includes('"specialDates"')) {
      try {
        return JSON.parse(schedulerByKey);
      } catch {
        const sanitizedByKey = this.sanitizeJsonLikeText(schedulerByKey);
        try {
          return JSON.parse(sanitizedByKey);
        } catch {
          // Continue with fallback strategies.
        }
      }
    }

    const schedulerFromSetCommand = this.extractSchedulerFromSetCommandFragment(payload);
    if (schedulerFromSetCommand) {
      try {
        return JSON.parse(schedulerFromSetCommand);
      } catch {
        const sanitizedSetCommand = this.sanitizeJsonLikeText(schedulerFromSetCommand);
        try {
          return JSON.parse(sanitizedSetCommand);
        } catch {
          // Continue with fallback strategies.
        }
      }
    }

    const marker = '"version"';
    const versionIdx = payload.indexOf(marker);
    if (versionIdx < 0) {
      return null;
    }

    const schedulerStart = payload.lastIndexOf('{', versionIdx);
    if (schedulerStart < 0) {
      return null;
    }

    const schedulerEnd = this.findMatchingClosingBrace(payload, schedulerStart);
    if (schedulerEnd < 0) {
      return null;
    }

    const candidate = payload.substring(schedulerStart, schedulerEnd + 1);
    if (!candidate.includes('"weeklySchedule"') || !candidate.includes('"specialDates"')) {
      return null;
    }

    try {
      return JSON.parse(candidate);
    } catch {
      const sanitizedCandidate = this.sanitizeJsonLikeText(candidate);
      try {
        return JSON.parse(sanitizedCandidate);
      } catch {
        return null;
      }
    }
  }

  private extractObjectByPropertyName(text: string, propertyName: string): string | null {
    const marker = `"${propertyName}"`;
    const propIdx = text.indexOf(marker);
    if (propIdx < 0) {
      return null;
    }

    const colonIdx = text.indexOf(':', propIdx + marker.length);
    if (colonIdx < 0) {
      return null;
    }

    const objectStart = text.indexOf('{', colonIdx + 1);
    if (objectStart < 0) {
      return null;
    }

    const objectEnd = this.findMatchingClosingBrace(text, objectStart);
    if (objectEnd < 0) {
      return null;
    }

    return text.substring(objectStart, objectEnd + 1);
  }

  private extractSchedulerFromSetCommandFragment(text: string): string | null {
    const marker = '"id":37,"scheduler"';
    const markerIdx = text.indexOf(marker);
    if (markerIdx < 0) {
      return null;
    }

    const schedulerKeyIdx = text.indexOf('"scheduler"', markerIdx);
    if (schedulerKeyIdx < 0) {
      return null;
    }

    const colonIdx = text.indexOf(':', schedulerKeyIdx + '"scheduler"'.length);
    if (colonIdx < 0) {
      return null;
    }

    const objectStart = text.indexOf('{', colonIdx + 1);
    if (objectStart < 0) {
      return null;
    }

    const objectEnd = this.findMatchingClosingBrace(text, objectStart);
    if (objectEnd < 0) {
      return null;
    }

    const candidate = text.substring(objectStart, objectEnd + 1);
    if (!candidate.includes('"weeklySchedule"') || !candidate.includes('"specialDates"')) {
      return null;
    }

    return candidate;
  }

  private sanitizeJsonLikeText(text: string): string {
    if (!text) {
      return text;
    }

    let out = text;
    out = out.replace(/([{,]\s*)([a-zA-Z_]\w*)\s*:/g, '$1"$2":');
    out = out.replace(/:\s*True(?=[,}\]\s])/g, ':true');
    out = out.replace(/:\s*False(?=[,}\]\s])/g, ':false');
    out = out.replace(/:\s*Null(?=[,}\]\s])/g, ':null');
    return out;
  }

  private findMatchingClosingBrace(text: string, openBraceIndex: number): number {
    let depth = 0;
    let inString = false;
    let escaped = false;

    for (let i = openBraceIndex; i < text.length; i++) {
      const ch = text[i];

      if (inString) {
        if (escaped) {
          escaped = false;
          continue;
        }
        if (ch === '\\') {
          escaped = true;
          continue;
        }
        if (ch === '"') {
          inString = false;
        }
        continue;
      }

      if (ch === '"') {
        inString = true;
        continue;
      }

      if (ch === '{') {
        depth++;
      } else if (ch === '}') {
        depth--;
        if (depth === 0) {
          return i;
        }
      }
    }

    return -1;
  }
}
